"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { Download, Pause, Play } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import { cn } from "@/lib/utils"

import { MediaDropzone } from "./media-dropzone"
import { SafeZoneOverlay } from "./safe-zone-checker"
import { SAFE_ZONE_SPECS } from "./safe-zone-specs"
import { useLocalMedia } from "./use-local-media"

/**
 * /tools/ad-mockup-generator widget.
 *
 * Composites the visitor's creative and copy into generic Feed / Stories /
 * Reels frames on a <canvas>, so the preview and the downloaded PNG are the
 * same pixels. The frames are deliberately NOT a copy of any platform's UI —
 * no platform logos, icons or typefaces — and every export carries a
 * "Preview approximation" label so it cannot pass as a real screenshot.
 *
 * Character guidance is Meta's Ads Guide "Text Recommendations", read
 * 2026-10-09: Instagram Feed 125 primary / 40 headline, Facebook Feed 50–150
 * primary / 27 headline, Instagram Stories 125 primary, Instagram Reels 44
 * primary. Nothing is uploaded: the file is a blob: URL in this tab.
 */

type Frame = "feed" | "stories" | "reels"
type FeedRatio = "4:5" | "1:1"

const CTA_OPTIONS = [
  "Shop now",
  "Learn more",
  "Sign up",
  "Download",
  "Book now",
  "Get offer",
  "Contact us",
  "Subscribe",
]

const GUIDANCE: Record<
  Frame,
  { primaryMax: number; primaryNote: string; headlineMax: number | null; headlineNote: string }
> = {
  feed: {
    primaryMax: 125,
    primaryNote: "Meta recommends 125 characters on Instagram Feed and 50–150 on Facebook Feed.",
    headlineMax: 40,
    headlineNote: "Meta recommends 40 characters on Instagram Feed and 27 on Facebook Feed.",
  },
  stories: {
    primaryMax: 125,
    primaryNote: "Meta recommends 125 characters of primary text for Instagram Stories.",
    headlineMax: null,
    headlineNote: "This Stories frame does not show a headline.",
  },
  reels: {
    primaryMax: 44,
    primaryNote: "Meta recommends 44 characters of primary text for Instagram Reels.",
    headlineMax: null,
    headlineNote: "This Reels frame does not show a headline.",
  },
}

const FONT = "system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif"
const W = 1080
const LABEL = "Preview approximation · real rendering varies by app and device"

type Source = HTMLImageElement | HTMLVideoElement

function sourceSize(src: Source): [number, number] {
  return src instanceof HTMLVideoElement
    ? [src.videoWidth, src.videoHeight]
    : [src.naturalWidth, src.naturalHeight]
}

/** Draw `src` into the box like CSS object-fit: cover. */
function drawCover(
  ctx: CanvasRenderingContext2D,
  src: Source | null,
  x: number,
  y: number,
  w: number,
  h: number,
) {
  const [sw, sh] = src ? sourceSize(src) : [0, 0]
  if (!src || !sw || !sh) {
    const g = ctx.createLinearGradient(x, y, x + w, y + h)
    g.addColorStop(0, "#3a2c20")
    g.addColorStop(1, "#16130f")
    ctx.fillStyle = g
    ctx.fillRect(x, y, w, h)
    ctx.fillStyle = "rgba(255,255,255,0.55)"
    ctx.font = `500 40px ${FONT}`
    ctx.textAlign = "center"
    ctx.fillText("Your creative goes here", x + w / 2, y + h / 2)
    ctx.textAlign = "left"
    return
  }
  const scale = Math.max(w / sw, h / sh)
  const dw = sw * scale
  const dh = sh * scale
  ctx.save()
  ctx.beginPath()
  ctx.rect(x, y, w, h)
  ctx.clip()
  ctx.drawImage(src, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh)
  ctx.restore()
}

/** Greedy word wrap; the last allowed line ends in "… more" when text overflows. */
function wrapLines(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxLines: number,
): string[] {
  const words = text.replace(/\s+/g, " ").trim().split(" ").filter(Boolean)
  const lines: string[] = []
  let line = ""
  for (const raw of words) {
    let word = raw
    // Hard-break a word wider than a whole line.
    while (ctx.measureText(word).width > maxWidth && word.length > 1) {
      let cut = word.length - 1
      while (cut > 1 && ctx.measureText(word.slice(0, cut)).width > maxWidth) cut--
      if (line) lines.push(line)
      lines.push(word.slice(0, cut))
      line = ""
      word = word.slice(cut)
    }
    const next = line ? `${line} ${word}` : word
    if (ctx.measureText(next).width <= maxWidth) {
      line = next
    } else {
      lines.push(line)
      line = word
    }
  }
  if (line) lines.push(line)
  if (lines.length <= maxLines) return lines

  const out = lines.slice(0, maxLines)
  const suffix = "… more"
  let last = out[maxLines - 1]
  while (last && ctx.measureText(`${last}${suffix}`).width > maxWidth) {
    last = last.slice(0, -1)
  }
  out[maxLines - 1] = `${last.trimEnd()}${suffix}`
  return out
}

function ellipsize(ctx: CanvasRenderingContext2D, text: string, maxWidth: number) {
  if (ctx.measureText(text).width <= maxWidth) return text
  let t = text
  while (t && ctx.measureText(`${t}…`).width > maxWidth) t = t.slice(0, -1)
  return `${t.trimEnd()}…`
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  ctx.beginPath()
  ctx.roundRect(x, y, w, h, r)
}

function avatar(
  ctx: CanvasRenderingContext2D,
  brand: string,
  cx: number,
  cy: number,
  r: number,
) {
  ctx.fillStyle = "#f97316"
  ctx.beginPath()
  ctx.arc(cx, cy, r, 0, Math.PI * 2)
  ctx.fill()
  ctx.fillStyle = "#1c1917"
  ctx.font = `700 ${Math.round(r)}px ${FONT}`
  ctx.textAlign = "center"
  ctx.textBaseline = "middle"
  ctx.fillText((brand.trim()[0] ?? "B").toUpperCase(), cx, cy + 1)
  ctx.textAlign = "left"
  ctx.textBaseline = "alphabetic"
}

interface Copy {
  brand: string
  primary: string
  headline: string
  cta: string
}

function drawFeed(
  canvas: HTMLCanvasElement,
  src: Source | null,
  copy: Copy,
  ratio: FeedRatio,
) {
  const ctx = canvas.getContext("2d")
  if (!ctx) return
  const pad = 36
  const mediaH = ratio === "4:5" ? 1350 : 1080
  ctx.font = `400 34px ${FONT}`
  const lines = copy.primary.trim()
    ? wrapLines(ctx, copy.primary, W - pad * 2, 3)
    : []
  const headerH = 150
  const textH = lines.length ? lines.length * 48 + 24 : 0
  const footerH = 170
  const actionsH = 110
  const labelH = 64
  const H = headerH + textH + mediaH + footerH + actionsH + labelH
  sizeCanvas(canvas, W, H)

  ctx.fillStyle = "#ffffff"
  ctx.fillRect(0, 0, W, H)

  avatar(ctx, copy.brand, pad + 44, 76, 44)
  ctx.fillStyle = "#111111"
  ctx.font = `600 36px ${FONT}`
  ctx.fillText(ellipsize(ctx, copy.brand || "Your Brand", W - 320), pad + 108, 68)
  ctx.fillStyle = "#6b7280"
  ctx.font = `400 28px ${FONT}`
  ctx.fillText("Sponsored", pad + 108, 110)
  ctx.fillStyle = "#9ca3af"
  for (let d = 0; d < 3; d++) {
    ctx.beginPath()
    ctx.arc(W - pad - 12 - d * 22, 76, 5, 0, Math.PI * 2)
    ctx.fill()
  }

  let y = headerH
  if (lines.length) {
    ctx.fillStyle = "#111111"
    ctx.font = `400 34px ${FONT}`
    lines.forEach((l, idx) => ctx.fillText(l, pad, y + 36 + idx * 48))
    y += textH
  }

  drawCover(ctx, src, 0, y, W, mediaH)
  y += mediaH

  ctx.fillStyle = "#f3f4f6"
  ctx.fillRect(0, y, W, footerH)
  const btnW = 260
  ctx.fillStyle = "#111111"
  ctx.font = `600 36px ${FONT}`
  const headline = copy.headline.trim() || "Your headline"
  ctx.fillText(ellipsize(ctx, headline, W - pad * 3 - btnW), pad, y + footerH / 2 + 12)
  roundRect(ctx, W - pad - btnW, y + (footerH - 84) / 2, btnW, 84, 16)
  ctx.fillStyle = "#e5e7eb"
  ctx.fill()
  ctx.fillStyle = "#111111"
  ctx.font = `600 32px ${FONT}`
  ctx.textAlign = "center"
  ctx.fillText(ellipsize(ctx, copy.cta, btnW - 24), W - pad - btnW / 2, y + footerH / 2 + 11)
  ctx.textAlign = "left"
  y += footerH

  ctx.strokeStyle = "#e5e7eb"
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.moveTo(pad, y + 1)
  ctx.lineTo(W - pad, y + 1)
  ctx.stroke()
  ctx.fillStyle = "#4b5563"
  ctx.font = `500 30px ${FONT}`
  ctx.textAlign = "center"
  ;["Like", "Comment", "Share"].forEach((t, idx) =>
    ctx.fillText(t, (W / 3) * idx + W / 6, y + actionsH / 2 + 10),
  )
  ctx.textAlign = "left"
  y += actionsH

  ctx.fillStyle = "#f9fafb"
  ctx.fillRect(0, y, W, labelH)
  ctx.fillStyle = "#9ca3af"
  ctx.font = `400 24px ${FONT}`
  ctx.textAlign = "center"
  ctx.fillText(LABEL, W / 2, y + 40)
  ctx.textAlign = "left"
}

// Assigning width/height clears and reallocates the backing bitmap even when
// the size is unchanged, which is expensive at 1080×1920 on every video frame.
function sizeCanvas(canvas: HTMLCanvasElement, width: number, height: number) {
  if (canvas.width !== width) canvas.width = width
  if (canvas.height !== height) canvas.height = height
}

function verticalBase(canvas: HTMLCanvasElement, src: Source | null) {
  const ctx = canvas.getContext("2d")
  if (!ctx) return null
  const H = 1920
  sizeCanvas(canvas, W, H)
  ctx.fillStyle = "#000"
  ctx.fillRect(0, 0, W, H)
  drawCover(ctx, src, 0, 0, W, H)
  const top = ctx.createLinearGradient(0, 0, 0, 320)
  top.addColorStop(0, "rgba(0,0,0,0.45)")
  top.addColorStop(1, "rgba(0,0,0,0)")
  ctx.fillStyle = top
  ctx.fillRect(0, 0, W, 320)
  const bottom = ctx.createLinearGradient(0, H - 760, 0, H)
  bottom.addColorStop(0, "rgba(0,0,0,0)")
  bottom.addColorStop(1, "rgba(0,0,0,0.7)")
  ctx.fillStyle = bottom
  ctx.fillRect(0, H - 760, W, 760)
  return { ctx, H }
}

function drawLabel(ctx: CanvasRenderingContext2D, H: number) {
  ctx.fillStyle = "rgba(255,255,255,0.6)"
  ctx.font = `400 24px ${FONT}`
  ctx.textAlign = "center"
  ctx.fillText(LABEL, W / 2, H - 28)
  ctx.textAlign = "left"
}

function drawStories(canvas: HTMLCanvasElement, src: Source | null, copy: Copy) {
  const base = verticalBase(canvas, src)
  if (!base) return
  const { ctx, H } = base
  const pad = 40

  roundRect(ctx, pad, 36, W - pad * 2, 8, 4)
  ctx.fillStyle = "rgba(255,255,255,0.4)"
  ctx.fill()
  roundRect(ctx, pad, 36, (W - pad * 2) * 0.35, 8, 4)
  ctx.fillStyle = "#ffffff"
  ctx.fill()

  avatar(ctx, copy.brand, pad + 40, 120, 40)
  ctx.fillStyle = "#ffffff"
  ctx.font = `600 34px ${FONT}`
  ctx.fillText(ellipsize(ctx, copy.brand || "Your Brand", W - 300), pad + 100, 112)
  ctx.font = `400 26px ${FONT}`
  ctx.fillStyle = "rgba(255,255,255,0.8)"
  ctx.fillText("Sponsored", pad + 100, 148)

  ctx.font = `400 34px ${FONT}`
  const lines = copy.primary.trim() ? wrapLines(ctx, copy.primary, W - 200, 2) : []
  ctx.fillStyle = "#ffffff"
  ctx.textAlign = "center"
  lines.forEach((l, idx) => ctx.fillText(l, W / 2, 1560 + idx * 48))

  // Swipe-up chevron + CTA pill.
  ctx.strokeStyle = "#ffffff"
  ctx.lineWidth = 5
  ctx.beginPath()
  ctx.moveTo(W / 2 - 22, 1690)
  ctx.lineTo(W / 2, 1670)
  ctx.lineTo(W / 2 + 22, 1690)
  ctx.stroke()
  const pillW = 380
  roundRect(ctx, (W - pillW) / 2, 1715, pillW, 90, 45)
  ctx.fillStyle = "#ffffff"
  ctx.fill()
  ctx.fillStyle = "#111111"
  ctx.font = `600 34px ${FONT}`
  ctx.fillText(ellipsize(ctx, copy.cta, pillW - 40), W / 2, 1772)
  ctx.textAlign = "left"
  drawLabel(ctx, H)
}

function drawReels(canvas: HTMLCanvasElement, src: Source | null, copy: Copy) {
  const base = verticalBase(canvas, src)
  if (!base) return
  const { ctx, H } = base
  const pad = 40

  // Generic action column — plain circles, no platform icons.
  for (let k = 0; k < 4; k++) {
    ctx.beginPath()
    ctx.arc(W - 80, 1060 + k * 130, 34, 0, Math.PI * 2)
    ctx.fillStyle = "rgba(255,255,255,0.22)"
    ctx.fill()
    ctx.strokeStyle = "rgba(255,255,255,0.85)"
    ctx.lineWidth = 3
    ctx.stroke()
  }

  avatar(ctx, copy.brand, pad + 36, 1500, 36)
  ctx.fillStyle = "#ffffff"
  ctx.font = `600 32px ${FONT}`
  ctx.fillText(ellipsize(ctx, copy.brand || "Your Brand", W - 360), pad + 90, 1494)
  ctx.font = `400 26px ${FONT}`
  ctx.fillStyle = "rgba(255,255,255,0.8)"
  ctx.fillText("Sponsored", pad + 90, 1530)

  ctx.font = `400 32px ${FONT}`
  ctx.fillStyle = "#ffffff"
  const lines = copy.primary.trim() ? wrapLines(ctx, copy.primary, W - 260, 2) : []
  lines.forEach((l, idx) => ctx.fillText(l, pad, 1610 + idx * 44))

  roundRect(ctx, pad, 1730, W - pad * 2, 96, 18)
  ctx.fillStyle = "rgba(255,255,255,0.92)"
  ctx.fill()
  ctx.fillStyle = "#111111"
  ctx.font = `600 34px ${FONT}`
  ctx.fillText(ellipsize(ctx, copy.cta, W - 260), pad + 36, 1790)
  ctx.strokeStyle = "#111111"
  ctx.lineWidth = 5
  ctx.beginPath()
  ctx.moveTo(W - pad - 52, 1762)
  ctx.lineTo(W - pad - 34, 1778)
  ctx.lineTo(W - pad - 52, 1794)
  ctx.stroke()
  drawLabel(ctx, H)
}

const META_VERTICAL_SAFE = SAFE_ZONE_SPECS.find(s => s.id === "meta-reels")!.safe

function CharCount({ value, max }: { value: string; max: number | null }) {
  if (max === null) return null
  const n = [...value].length
  return (
    <span
      className={cn(
        "font-mono text-xs",
        n > max ? "text-amber-400" : "text-white/40",
      )}
    >
      {n} / {max}
    </span>
  )
}

export function AdMockupGenerator() {
  const { media, error, pick, setDimensions, clear, fail } = useLocalMedia()
  const [frame, setFrame] = useState<Frame>("feed")
  const [ratio, setRatio] = useState<FeedRatio>("4:5")
  const [brand, setBrand] = useState("Your Brand")
  const [primary, setPrimary] = useState(
    "Refillable cleaning spray that ships flat. Three scents, one glass bottle you keep — refills cost a third of a new one.",
  )
  const [headline, setHeadline] = useState("Starter kit, free shipping")
  const [cta, setCta] = useState(CTA_OPTIONS[0])
  const [showSafe, setShowSafe] = useState(false)
  // Both keyed by the file URL, so picking a new file resets them without a
  // setState-in-effect: a stale entry simply stops matching.
  const [playingUrl, setPlayingUrl] = useState<string | null>(null)
  const [decoded, setDecoded] = useState<{ url: string; el: Source } | null>(
    null,
  )

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)

  const mediaUrl = media?.url
  const mediaKind = media?.kind
  const source = decoded && decoded.url === mediaUrl ? decoded.el : null
  const playing = Boolean(mediaUrl) && playingUrl === mediaUrl

  // Decode a picked image into a drawable element.
  useEffect(() => {
    if (!mediaUrl || mediaKind !== "image") return
    const img = new Image()
    // A slow decode for a replaced file must not land after the new one.
    let stale = false
    img.onload = () => {
      if (stale) return
      setDimensions(img.naturalWidth, img.naturalHeight)
      setDecoded({ url: mediaUrl, el: img })
    }
    img.onerror = () => {
      if (!stale) fail()
    }
    img.src = mediaUrl
    // Video is handled by the <video> element below.
    return () => {
      stale = true
      img.onload = null
      img.onerror = null
    }
  }, [mediaUrl, mediaKind, setDimensions, fail])

  const draw = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const copy = { brand, primary, headline, cta }
    if (frame === "feed") drawFeed(canvas, source, copy, ratio)
    else if (frame === "stories") drawStories(canvas, source, copy)
    else drawReels(canvas, source, copy)
  }, [brand, primary, headline, cta, frame, ratio, source])

  useEffect(() => {
    draw()
  }, [draw])

  // Redraw every frame while a video plays.
  useEffect(() => {
    if (!playing) return
    let raf = 0
    const tick = () => {
      draw()
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [playing, draw])

  const togglePlay = () => {
    const v = videoRef.current
    if (!v) return
    if (v.paused) {
      const url = mediaUrl ?? null
      setPlayingUrl(url)
      // play() rejects on an unsupported codec or an interrupted load; drop
      // back to the paused state so the redraw loop stops.
      v.play().catch(() =>
        setPlayingUrl(current => (current === url ? null : current)),
      )
    } else {
      v.pause()
      setPlayingUrl(null)
    }
  }

  const download = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    draw()
    canvas.toBlob(blob => {
      if (!blob) return
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = `ad-mockup-${frame}.png`
      a.click()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    }, "image/png")
  }

  const g = GUIDANCE[frame]
  const vertical = frame !== "feed"

  return (
    <div className="grid gap-8 rounded-3xl border border-white/10 bg-white/[0.03] p-5 text-left sm:p-8 lg:grid-cols-[1fr_minmax(0,380px)]">
      <div className="order-2 space-y-5 lg:order-1">
        <MediaDropzone
          onFile={pick}
          hasMedia={Boolean(media)}
          fileName={media?.name}
          onClear={clear}
          error={error}
        />

        <div>
          <Label className="text-sm text-white/80">Frame</Label>
          <ToggleGroup
            type="single"
            value={frame}
            onValueChange={v => v && setFrame(v as Frame)}
            variant="outline"
            className="mt-3"
          >
            <ToggleGroupItem value="feed" className="px-4 text-sm">
              Feed
            </ToggleGroupItem>
            <ToggleGroupItem value="stories" className="px-4 text-sm">
              Stories
            </ToggleGroupItem>
            <ToggleGroupItem value="reels" className="px-4 text-sm">
              Reels
            </ToggleGroupItem>
          </ToggleGroup>
        </div>

        {frame === "feed" ? (
          <div>
            <Label className="text-sm text-white/80">Feed media shape</Label>
            <ToggleGroup
              type="single"
              value={ratio}
              onValueChange={v => v && setRatio(v as FeedRatio)}
              variant="outline"
              className="mt-3"
            >
              <ToggleGroupItem value="4:5" className="px-4 text-sm">
                4:5
              </ToggleGroupItem>
              <ToggleGroupItem value="1:1" className="px-4 text-sm">
                1:1
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <Switch
              id="mockup-safe-zone"
              checked={showSafe}
              onCheckedChange={setShowSafe}
            />
            <Label htmlFor="mockup-safe-zone" className="text-sm text-white/70">
              Show Meta&rsquo;s 9:16 safe zone (not exported)
            </Label>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="mockup-brand" className="text-sm text-white/80">
            Brand name
          </Label>
          <Input
            id="mockup-brand"
            value={brand}
            maxLength={60}
            onChange={e => setBrand(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="mockup-primary" className="text-sm text-white/80">
              Primary text
            </Label>
            <CharCount value={primary} max={g.primaryMax} />
          </div>
          <Textarea
            id="mockup-primary"
            value={primary}
            rows={3}
            maxLength={2200}
            onChange={e => setPrimary(e.target.value)}
          />
          <p className="text-xs text-white/40">{g.primaryNote}</p>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="mockup-headline" className="text-sm text-white/80">
              Headline
            </Label>
            <CharCount value={headline} max={g.headlineMax} />
          </div>
          <Input
            id="mockup-headline"
            value={headline}
            maxLength={255}
            onChange={e => setHeadline(e.target.value)}
            disabled={vertical}
          />
          <p className="text-xs text-white/40">{g.headlineNote}</p>
        </div>

        <div className="space-y-2">
          <Label className="text-sm text-white/80">Call-to-action button</Label>
          <Select value={cta} onValueChange={setCta}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CTA_OPTIONS.map(option => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="order-1 lg:order-2">
        <div className="relative mx-auto w-full max-w-[380px] overflow-hidden rounded-2xl border border-white/15 bg-black">
          <canvas
            ref={canvasRef}
            className="block h-auto w-full"
            aria-label={`Ad mockup preview, ${frame} frame`}
            role="img"
          />
          {vertical && showSafe ? (
            <SafeZoneOverlay safe={META_VERTICAL_SAFE} opacity={0.35} outline />
          ) : null}
        </div>
        {media?.kind === "video" ? (
          <video
            ref={videoRef}
            key={media.url}
            src={media.url}
            muted
            loop
            playsInline
            // Kept rendered (not display:none) so browsers keep decoding frames.
            className="pointer-events-none absolute h-px w-px opacity-0"
            onLoadedData={e => {
              setDimensions(
                e.currentTarget.videoWidth,
                e.currentTarget.videoHeight,
              )
              setDecoded({ url: media.url, el: e.currentTarget })
            }}
            onSeeked={() => draw()}
            onError={fail}
          />
        ) : null}
        <div className="mx-auto mt-4 flex max-w-[380px] flex-wrap justify-center gap-2">
          {media?.kind === "video" ? (
            <Button type="button" variant="secondary" size="sm" onClick={togglePlay}>
              {playing ? (
                <>
                  <Pause className="size-4" /> Pause
                </>
              ) : (
                <>
                  <Play className="size-4" /> Play
                </>
              )}
            </Button>
          ) : null}
          <Button
            type="button"
            size="sm"
            onClick={download}
            disabled={!source}
            className="bg-orange-500 text-stone-950 hover:bg-orange-400"
          >
            <Download className="size-4" /> Download PNG
          </Button>
        </div>
        <p className="mx-auto mt-3 max-w-[380px] text-center text-xs leading-5 text-white/45">
          A generic frame, not the platform&rsquo;s interface. Real ads render
          differently by app, device, placement and text length.
          {media?.kind === "video" ? " Video downloads as the current frame." : ""}
        </p>
      </div>
    </div>
  )
}
